import * as THREE from 'three';
import { beveledBox, box, cylinderN, mergeParts, part, type Part } from '@/art/geometry';
import { emissive, vertexColored } from '@/art/materials';

export const VEHICLES = {
  sedan: {
    width: 2.18,
    length: 4.8,
    height: 1.67,
    bodyWidth: 1.72,
    wheelRadius: 0.34,
    wheelWidth: 0.28,
    triBudget: 1750,
  },
  van: {
    width: 2.28,
    length: 5.55,
    height: 2.0,
    bodyWidth: 1.88,
    wheelRadius: 0.36,
    wheelWidth: 0.3,
    triBudget: 1850,
  },
  wreck: {
    width: 2.28,
    length: 4.9,
    height: 1.18,
    bodyWidth: 1.86,
    wheelRadius: 0.33,
    wheelWidth: 0.28,
    triBudget: 1500,
  },
} as const;

export type VehicleKind = 'sedan' | 'van' | 'wreck';

export function buildVehicle(kind: VehicleKind): THREE.Group {
  switch (kind) {
    case 'sedan':
      return buildSedan();
    case 'van':
      return buildVan();
    case 'wreck':
      return buildWreck();
  }
}

export function buildSedan(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'NA_Vehicle_Sedan';
  const v = VEHICLES.sedan;

  const shell: Part[] = [
    part(beveledBox(v.bodyWidth, 0.58, v.length, 0.04).translate(0, 0.66, 0), 'slateGreen'),
    part(beveledBox(v.bodyWidth * 0.9, 0.34, 1.34, 0.035).translate(0, 0.98, 1.18), 'slateGreen'),
    part(beveledBox(v.bodyWidth * 0.92, 0.34, 1.22, 0.035).translate(0, 0.96, -1.56), 'slateGreen'),
    part(beveledBox(v.bodyWidth * 0.72, 0.68, 1.48, 0.04).translate(0, 1.26, -0.24), 'deepTeal'),
    part(box(v.bodyWidth * 0.62, 0.08, 1.1).translate(0, 1.63, -0.24), 'charcoal'),
    part(box(v.bodyWidth * 0.62, 0.12, 0.08).translate(0, 1.34, 0.62), 'windowCold'),
    part(box(v.bodyWidth * 0.58, 0.18, 0.07).translate(0, 1.28, -1.0), 'windowCold'),
    part(box(0.08, 0.34, 0.92).translate(-0.67, 1.23, -0.22), 'windowCold'),
    part(box(0.08, 0.34, 0.92).translate(0.67, 1.23, -0.22), 'windowCold'),
    part(box(0.08, 0.38, 1.14).translate(-0.33, 1.26, -0.22), 'darkSteel'),
    part(box(0.08, 0.38, 1.14).translate(0.33, 1.26, -0.22), 'darkSteel'),
    part(box(v.bodyWidth * 0.78, 0.06, 0.08).translate(0, 1.48, 0.5), 'darkSteel'),
    part(box(v.bodyWidth * 0.72, 0.06, 0.08).translate(0, 1.42, -0.88), 'darkSteel'),
    part(box(v.bodyWidth + 0.18, 0.08, 0.08).translate(0, 0.93, 2.01), 'darkSteel'),
    part(box(v.bodyWidth + 0.1, 0.08, 0.08).translate(0, 0.9, -2.02), 'darkSteel'),
    part(box(0.06, 0.08, v.length * 0.72).translate(-v.bodyWidth / 2 - 0.03, 0.72, -0.04), 'darkSteel'),
    part(box(0.06, 0.08, v.length * 0.72).translate(v.bodyWidth / 2 + 0.03, 0.72, -0.04), 'darkSteel'),
    part(box(0.08, 0.08, v.length * 0.54).translate(-v.bodyWidth / 2 - 0.03, 1.62, -0.22), 'darkSteel'),
    part(box(0.08, 0.08, v.length * 0.54).translate(v.bodyWidth / 2 + 0.03, 1.62, -0.22), 'darkSteel'),
    part(box(0.16, 0.1, 0.22).translate(-v.bodyWidth / 2 - 0.12, 1.06, 0.72), 'darkSteel'),
    part(box(0.16, 0.1, 0.22).translate(v.bodyWidth / 2 + 0.12, 1.06, 0.72), 'darkSteel'),
    part(box(v.bodyWidth * 0.54, 0.12, 0.08).translate(0, 0.75, 2.44), 'slateGrey'),
    part(box(0.08, 0.24, 0.08).translate(-0.32, 0.75, 2.45), 'darkSteel'),
    part(box(0.08, 0.24, 0.08).translate(0.32, 0.75, 2.45), 'darkSteel'),
    ...wheelArchParts(v.width, v.length, v.wheelRadius, 'slateGreen'),
    ...wheelParts(v.width, v.length, v.wheelRadius, v.wheelWidth, 'charcoal'),
    ...bumperParts(v.bodyWidth, v.length),
  ];

  addVehicleMeshes(group, shell, v.length, true);
  return group;
}

export function buildVan(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'NA_Vehicle_Van';
  const v = VEHICLES.van;

  const shell: Part[] = [
    part(beveledBox(v.bodyWidth, 1.05, v.length, 0.045).translate(0, 0.9, 0), 'oxideMaroon'),
    part(beveledBox(v.bodyWidth * 0.9, 0.66, 2.05, 0.04).translate(0, 1.58, 0.98), 'oxideMaroon'),
    part(box(v.bodyWidth * 0.62, 0.52, 0.12).translate(0, 1.56, 2.84), 'deepTeal'),
    part(box(v.bodyWidth * 0.48, 0.45, 0.1).translate(-0.51, 1.5, 0.62), 'charcoal'),
    part(box(v.bodyWidth * 0.48, 0.45, 0.1).translate(0.51, 1.5, 0.62), 'charcoal'),
    part(box(0.1, 0.64, 1.7).translate(-v.bodyWidth / 2 - 0.02, 1.28, -1.14), 'nightStone'),
    part(box(0.1, 0.64, 1.7).translate(v.bodyWidth / 2 + 0.02, 1.28, -1.14), 'nightStone'),
    part(box(0.1, 0.46, 1.46).translate(-v.bodyWidth / 2 - 0.04, 1.56, -1.0), 'windowCold'),
    part(box(0.1, 0.46, 1.46).translate(v.bodyWidth / 2 + 0.04, 1.56, -1.0), 'windowCold'),
    part(box(0.08, 0.5, 1.55).translate(-0.36, 1.56, 0.58), 'darkSteel'),
    part(box(0.08, 0.5, 1.55).translate(0.36, 1.56, 0.58), 'darkSteel'),
    part(box(v.bodyWidth + 0.12, 0.1, 0.1).translate(0, 1.36, 2.14), 'darkSteel'),
    part(box(v.bodyWidth + 0.16, 0.1, 0.1).translate(0, 0.86, 2.38), 'darkSteel'),
    part(box(v.bodyWidth * 0.44, 0.22, 0.08).translate(0, 0.88, 2.86), 'darkSteel'),
    part(box(0.07, 0.08, v.length * 0.68).translate(-v.bodyWidth / 2 - 0.05, 1.16, -0.26), 'darkSteel'),
    part(box(0.07, 0.08, v.length * 0.68).translate(v.bodyWidth / 2 + 0.05, 1.16, -0.26), 'darkSteel'),
    part(box(0.18, 0.11, 0.24).translate(-v.bodyWidth / 2 - 0.13, 1.38, 1.42), 'darkSteel'),
    part(box(0.18, 0.11, 0.24).translate(v.bodyWidth / 2 + 0.13, 1.38, 1.42), 'darkSteel'),
    part(box(v.bodyWidth * 0.78, 0.08, 0.08).translate(0, 1.96, 0.1), 'darkSteel'),
    part(box(v.bodyWidth * 0.82, 0.12, 0.08).translate(0, 0.9, -2.82), 'darkSteel'),
    part(box(0.62, 0.18, 0.08).translate(-0.46, 0.9, -2.84), 'slateGrey'),
    part(box(0.62, 0.18, 0.08).translate(0.46, 0.9, -2.84), 'slateGrey'),
    ...wheelArchParts(v.width, v.length, v.wheelRadius, 'oxideMaroon'),
    ...wheelParts(v.width, v.length, v.wheelRadius, v.wheelWidth, 'charcoal'),
    ...bumperParts(v.bodyWidth, v.length),
  ];

  addVehicleMeshes(group, shell, v.length, true);
  return group;
}

export function buildWreck(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'NA_Vehicle_Wreck';
  const v = VEHICLES.wreck;

  const shell: Part[] = [
    part(beveledBox(v.bodyWidth, 0.48, v.length, 0.04).translate(0, 0.58, 0), 'rustOrange'),
    part(beveledBox(v.bodyWidth * 0.72, 0.48, 1.42, 0.035).translate(-0.12, 1.02, -0.28), 'nightBrick'),
    part(box(v.bodyWidth * 0.78, 0.18, 1.16).translate(0.14, 0.86, 1.52), 'darkSteel'),
    part(box(v.bodyWidth * 0.92, 0.2, 1.0).translate(-0.08, 0.78, -1.75), 'nightStone'),
    part(box(0.72, 0.08, 0.82).translate(-0.34, 1.26, -0.32), 'charcoal'),
    part(box(v.bodyWidth + 0.1, 0.07, 0.08).translate(0, 0.76, 2.04), 'darkSteel'),
    part(box(0.08, 0.08, v.length * 0.58).rotateZ(0.04).translate(-v.bodyWidth / 2 - 0.04, 0.68, -0.18), 'darkSteel'),
    part(box(0.08, 0.08, v.length * 0.54).rotateZ(-0.06).translate(v.bodyWidth / 2 + 0.04, 0.68, -0.08), 'darkSteel'),
    part(box(v.bodyWidth * 0.46, 0.08, 0.07).rotateZ(0.08).translate(0.08, 1.24, -0.74), 'charcoal'),
    part(box(0.18, 0.12, 0.08).translate(-0.62, 0.74, 2.42), 'darkSteel'),
    ...wheelArchParts(v.width, v.length, v.wheelRadius, 'rustOrange'),
    ...wheelParts(v.width, v.length, v.wheelRadius, v.wheelWidth, 'charcoal', false),
    part(cylinderWheel(v.wheelRadius, v.wheelWidth).translate(v.width / 2 - v.wheelWidth / 2, 0.32, -1.42), 'darkSteel'),
  ];

  const debris: Part[] = [
    part(box(0.9, 0.08, 0.46).translate(-0.68, 0.08, 2.2), 'rustOrange'),
    part(box(0.42, 0.12, 0.34).translate(0.78, 0.12, -2.18), 'darkSteel'),
  ];

  addVehicleMeshes(group, [...shell, ...debris], v.length, false);
  return group;
}

function addVehicleMeshes(group: THREE.Group, shellParts: Part[], length: number, intactLights: boolean): void {
  const shell = new THREE.Mesh(mergeParts(shellParts), vertexColored());
  shell.name = 'shell';
  shell.castShadow = true;
  shell.receiveShadow = true;
  group.add(shell);

  if (!intactLights) return;

  const headlights = new THREE.Mesh(
    mergeParts([
      part(box(0.34, 0.16, 0.06).translate(-0.48, 0.74, length / 2 + 0.04), 'headlight'),
      part(box(0.34, 0.16, 0.06).translate(0.48, 0.74, length / 2 + 0.04), 'headlight'),
    ]),
    emissive('headlight', 1.1),
  );
  headlights.name = 'headlights';
  group.add(headlights);

  const taillights = new THREE.Mesh(
    mergeParts([
      part(box(0.28, 0.14, 0.06).translate(-0.52, 0.72, -length / 2 - 0.04), 'tailLight'),
      part(box(0.28, 0.14, 0.06).translate(0.52, 0.72, -length / 2 - 0.04), 'tailLight'),
    ]),
    emissive('tailLight', 1.05),
  );
  taillights.name = 'taillights';
  group.add(taillights);
}

function wheelParts(
  width: number,
  length: number,
  radius: number,
  wheelWidth: number,
  color: 'charcoal' | 'darkSteel',
  rearLeft = true,
): Part[] {
  const x = width / 2 - wheelWidth / 2;
  const z = length / 2 - 0.92;
  const entries = [
    [-x, z],
    [x, z],
    rearLeft ? [-x, -z] : null,
    [x, -z],
  ].filter((entry): entry is [number, number] => entry !== null);
  return entries.map(([wheelX, wheelZ]) => (
    part(cylinderWheel(radius, wheelWidth).translate(wheelX, radius, wheelZ), color)
  ));
}

function bumperParts(bodyWidth: number, length: number): Part[] {
  return [
    part(box(bodyWidth + 0.18, 0.16, 0.16).translate(0, 0.52, length / 2 + 0.04), 'darkSteel'),
    part(box(bodyWidth + 0.18, 0.16, 0.16).translate(0, 0.52, -length / 2 - 0.04), 'darkSteel'),
  ];
}

function wheelArchParts(
  width: number,
  length: number,
  radius: number,
  color: 'slateGreen' | 'oxideMaroon' | 'rustOrange',
): Part[] {
  const x = width / 2 - 0.16;
  const z = length / 2 - 0.92;
  const archWidth = 0.14;
  const archHeight = radius * 0.78;
  const archDepth = radius * 1.95;
  return [
    part(beveledBox(archWidth, archHeight, archDepth, 0.025).translate(-x, radius + archHeight * 0.45, z), color),
    part(beveledBox(archWidth, archHeight, archDepth, 0.025).translate(x, radius + archHeight * 0.45, z), color),
    part(beveledBox(archWidth, archHeight, archDepth, 0.025).translate(-x, radius + archHeight * 0.45, -z), color),
    part(beveledBox(archWidth, archHeight, archDepth, 0.025).translate(x, radius + archHeight * 0.45, -z), color),
  ];
}

function cylinderWheel(radius: number, width: number): THREE.BufferGeometry {
  const wheel = cylinderN(radius, width, 16);
  wheel.rotateZ(Math.PI / 2);
  return wheel;
}
